import type { DayOfWeek, MealType } from '../types';
import { DAYS_OF_WEEK } from '../types';

const MEAL_ORDER: readonly MealType[] = ['Breakfast', 'Lunch', 'Dinner'];

const DEFAULT_VISIBLE_MEALS: MealType[] = ['Lunch', 'Dinner'];

/** Normalize household visibleMealTypes (array or legacy object) into ordered meal list. */
export function normalizeVisibleMealTypes(
    raw: unknown,
    fallback: MealType[] = DEFAULT_VISIBLE_MEALS,
): MealType[] {
    let selected: MealType[] = [];

    if (Array.isArray(raw)) {
        selected = raw.filter((meal): meal is MealType =>
            MEAL_ORDER.includes(meal as MealType),
        );
    } else if (raw && typeof raw === 'object') {
        selected = MEAL_ORDER.filter(
            (meal) => (raw as Record<string, boolean>)[meal] === true,
        );
    }

    return selected.length > 0 ? [...selected] : [...fallback];
}

export { MEAL_ORDER };

/** Hour (0–23) after which the meal slot is considered passed for "next meal" defaults. */
const MEAL_CUTOFF_HOUR: Record<MealType, number> = {
    Breakfast: 11,
    Lunch: 16,
    Dinner: 22,
};

export function getTodayDayOfWeek(date = new Date()): DayOfWeek {
    return DAYS_OF_WEEK[date.getDay()];
}

export function getNextMealType(
    visibleMealTypes: readonly MealType[],
    date = new Date(),
): MealType {
    const ordered = MEAL_ORDER.filter((meal) => visibleMealTypes.includes(meal));
    if (ordered.length === 0) return 'Dinner';

    const hour = date.getHours();
    const nextToday = ordered.find((meal) => hour < MEAL_CUTOFF_HOUR[meal]);
    return nextToday ?? ordered[0];
}

export function getDefaultScheduleSlot(
    visibleMealTypes: readonly MealType[],
    date = new Date(),
): { day: DayOfWeek; mealType: MealType } {
    const ordered = MEAL_ORDER.filter((meal) => visibleMealTypes.includes(meal));
    if (ordered.length === 0) {
        return { day: getTodayDayOfWeek(date), mealType: 'Dinner' };
    }

    const hour = date.getHours();
    const lastMeal = ordered[ordered.length - 1];
    const allMealsPassedToday = hour >= MEAL_CUTOFF_HOUR[lastMeal];

    if (allMealsPassedToday) {
        const tomorrow = new Date(date);
        tomorrow.setDate(date.getDate() + 1);
        return { day: getTodayDayOfWeek(tomorrow), mealType: ordered[0] };
    }

    return {
        day: getTodayDayOfWeek(date),
        mealType: getNextMealType(visibleMealTypes, date),
    };
}
